import React, { useState, useEffect } from 'react'
import { Modal, Button, Select, message } from 'antd'
import CustomSelectField from '../../../../../../components/forms/CustomSelectField'
import GooglePlacesAutocomplete from '../../../../../../components/forms/GooglePlacesAutocomplete'
import SimpleTable, { StatusPill } from '../../../../../../components/UI/Tables/SimpleTable'
import CustomModal from '../../../../../../components/UI/Modals/CustomModal'
import { useGetTeamsQuery, useAddTeamsMutation, useDeleteTeamMutation } from '../../../../../../services/api/team/teamApi'
import { useGetLocationsQuery, useAddLocationMutation, useToggleLocationStatusMutation, useUpdateLocationMutation } from '../../../../../../services/api/location/locationApi'
import { useGetStatesQuery } from '../../../../../../services/api/states/stateApi'
import Loader from '@/components/UI/Loader/Loader'
import { useGetLGAsQuery } from '@/services/api/lgas/lgaApi'
import { LoadScript } from '@react-google-maps/api'
import useAuth from "@/hooks/useAuth";

const TeamsandLocations = () => {
    const authUser = useAuth();
  const [selectedBranches, setSelectedBranches] = useState([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [newTeamName, setNewTeamName] = useState('')
  const [newTeamDescription, setNewTeamDescription] = useState('')
  const [teamToDelete, setTeamToDelete] = useState(null)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)

  // Location modal state
  const [isLocationModalVisible, setIsLocationModalVisible] = useState(false)
  const [locationModalMode, setLocationModalMode] = useState('create') // 'create' | 'edit' | 'view'
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [locationFormData, setLocationFormData] = useState({
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    state_id: null,
    lga_id: null,
    phone_number: '',
    status: 1,
    created_by: authUser?.id
  })

  // Location status toggle modal state
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false)
  const [locationToToggle, setLocationToToggle] = useState(null)

  // API hooks
  const { data: teamsData, isLoading: teamsLoading, error: teamsError, refetch: refetchTeams } = useGetTeamsQuery()
  const [addTeam, { isLoading: addTeamLoading }] = useAddTeamsMutation()
  const [deleteTeam, { isLoading: deleteTeamLoading }] = useDeleteTeamMutation()

  // Location API hooks
  const { data: locationsData, isLoading: locationsLoading, error: locationsError, refetch: refetchLocations, isFetching: locationsFetching } = useGetLocationsQuery()
  const [addLocation, { isLoading: addLocationLoading }] = useAddLocationMutation()
  const [updateLocation, { isLoading: updateLocationLoading }] = useUpdateLocationMutation()
  const [toggleLocationStatus, { isLoading: toggleLocationLoading }] = useToggleLocationStatusMutation()

  // States API hooks
  const { data: statesData, isLoading: statesLoading, error: statesError } = useGetStatesQuery()
  const [addressSearchValue, setAddressSearchValue] = useState("");
  const [addressOptions, setAddressOptions] = useState([]);
  const autocompleteServiceRef = React.useRef(null);

  // LGAs API hooks - only fetch when state_id is selected
  const {
    data: lgasData,
    isLoading: isLoadingLgas,
    error: lgasError,
    isFetching: isFetchingLgas
  } = useGetLGAsQuery(locationFormData.state_id, {
    skip: !locationFormData.state_id
  })

  // Get locations from API
  const locations = locationsData || []

  // Format locations for table
  const branchesData = locations.map((location, index) => ({
    key: location.id || index,
    id: location.id,
    lga: location.lga?.name || location.name,
    lga_id: location.lga_id,
    state: location.state?.name || 'N/A',
    state_id: location.state_id,
    address: location.address,
    phone: location.phone_number,
    latitude: location.latitude,
    longitude: location.longitude,
    name: location.name,
    status: location.status === 1 ? 'Active' : 'Inactive',
    rawStatus: location.status,
    rawData: location // Keep the raw data for editing
  }))

  const branchColumns = [
    {
      title: 'Location',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <StatusPill status={status} />
    }
  ]

  // Location modal handlers
  const handleOpenLocationModal = (mode = 'create', record = null) => {
    setLocationModalMode(mode)
    setSelectedLocation(record)

    if (mode === 'create') {
      setLocationFormData({
        name: '',
        address: '',
        latitude: '',
        longitude: '',
        state_id: null,
        lga_id: null,
        phone_number: '',
        status: 1,
        created_by: authUser?.id 
      })
    } else if (mode === 'edit' || mode === 'view') {
      // Populate form with existing data
      setLocationFormData({
        name: record?.rawData?.name || record?.lga || '',
        address: record?.address || '',
        latitude: record?.latitude?.toString() || '',
        longitude: record?.longitude?.toString() || '',
        state_id:  record?.rawData?.state?.id || null,
        lga_id: record?.rawData?.lga?.id || null,
        phone_number: record?.phone || '',
        status: record?.rawStatus ?? 1,
        created_by: record?.rawData?.created_by
      })
    }

    setIsLocationModalVisible(true)
  }

  const handleCloseLocationModal = () => {
    setIsLocationModalVisible(false)
    setLocationModalMode('create')
    setSelectedLocation(null)
    setLocationFormData({
      name: '',
      address: '',
      latitude: '',
      longitude: '',
      state_id: null,
      lga_id: null,
      phone_number: '',
      status: 1,
      created_by: authUser?.id
    })
  }

  const handleLocationInputChange = (field, value) => {
    setLocationFormData(prev => {
      if (field === 'state_id') {
        return {
          ...prev,
          [field]: value,
          lga_id: null
        }
      }
      return {
        ...prev,
        [field]: value
      }
    })
  }

  const handleSaveLocation = async () => {
    try {
      const locationData = {
        ...locationFormData,
        latitude: parseFloat(locationFormData.latitude),
        longitude: parseFloat(locationFormData.longitude),
        status: parseInt(locationFormData.status)
      }

      if (locationModalMode === 'create') {
        await addLocation(locationData).unwrap()
        message.success('Location added successfully')
      } else if (locationModalMode === 'edit') {
        await updateLocation({ 
        id: selectedLocation.id, 
        requestData: locationData 
      }).unwrap()

        message.success('Location updated successfully')
      }

      handleCloseLocationModal()
      refetchLocations()
    } catch (error) {
      message.error(error?.data?.message || 'Failed to save location. Please try again.')
    }
  }

  // Handle Google Places selection
  const handlePlaceSelect = (placeData) => {
    setLocationFormData(prev => ({
      ...prev,
      address: placeData.address,
      latitude: placeData.latitude.toString(),
      longitude: placeData.longitude.toString()
    }))
  }

  const handleEditBranch = (record) => {
    handleOpenLocationModal('edit', record)
  }

  const handleViewBranch = (record) => {
    handleOpenLocationModal('view', record)
  }

  const handleDeactivateLocation = (record) => {
    setLocationToToggle(record)
    setIsStatusModalVisible(true)
  }

  const handleConfirmStatusToggle = async () => {
    try {
      await toggleLocationStatus(locationToToggle.key).unwrap()
      message.success(`Location ${locationToToggle.status === 'Active' ? 'deactivated' : 'activated'} successfully`)
      refetchLocations()
      setIsStatusModalVisible(false)
      setLocationToToggle(null)
    } catch (error) {
      message.error('Failed to update location status')
    }
  }

  const handleCancelStatusToggle = () => {
    setIsStatusModalVisible(false)
    setLocationToToggle(null)
  }

  const handleSelectBranches = (selectedRowKeys, selectedRows) => {
    setSelectedBranches(selectedRowKeys)
  }

  // Get teams from API response and format for select
  const teams = teamsData || []
  const teamOptions = teams.map(team => ({
    value: team.id,
    label: team.name
  }))

  // Get states for location modal
  const states = statesData || []
  const stateOptions = states.map(state => ({
    value: state.id,
    label: state.name
  }))

  // Get LGAs for location modal
  const lgas = lgasData || []
  const lgaOptions = lgas.map(lga => ({
    value: lga.id,
    label: lga.name
  }))

  // Auto-select all teams
  const selectedTeams = teams.map(team => team.id)

  const handleOpenModal = () => {
    setIsModalVisible(true)
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
    setNewTeamName('')
    setNewTeamDescription('')
  }

  const handleAddTeam = async () => {
    if (newTeamName.trim()) {
      try {
        const teamData = {
          name: newTeamName.trim(),
          description: newTeamDescription.trim() || 'Team description'
        }

        await addTeam(teamData).unwrap()
        handleCloseModal()
        refetchTeams()
        message.success('Team added successfully')
      } catch (error) {
        message.error('Failed to add team')
      }
    }
  }

  const handleDeleteTeam = async (teamId) => {
    try {
      await deleteTeam(teamId).unwrap()
      refetchTeams()
      setIsDeleteModalVisible(false)
      setTeamToDelete(null)
      message.success('Team deleted successfully')
    } catch (error) {
      message.error(error.data?.message ?? 'Failed to delete team. Please try again.')
    }
  }

  const showDeleteConfirmation = (teamId) => {
    const team = teams.find(t => t.id === teamId)
    setTeamToDelete(team)
    setIsDeleteModalVisible(true)
  }

  const handleTeamChange = (newSelectedTeams) => {
    const removedTeamId = selectedTeams.find(id => !newSelectedTeams.includes(id))
    if (removedTeamId) {
      showDeleteConfirmation(removedTeamId)
    }
  }

  // Modal title and button text based on mode
  const getModalTitle = () => {
    switch (locationModalMode) {
      case 'create':
        return 'Add New Location'
      case 'edit':
        return 'Edit Location'
      case 'view':
        return 'View Location'
      default:
        return 'Location'
    }
  }

  const getModalSubtitle = () => {
    switch (locationModalMode) {
      case 'create':
        return 'Enter the details for the new location'
      case 'edit':
        return 'Update the location details'
      case 'view':
        return 'Location details'
      default:
        return ''
    }
  }

  const getPrimaryActionText = () => {
    switch (locationModalMode) {
      case 'create':
        return 'Add Location'
      case 'edit':
        return 'Save Changes'
      case 'view':
        return 'Edit'
      default:
        return 'Save'
    }
  }

  const handlePrimaryAction = () => {
    if (locationModalMode === 'view') {
      setLocationModalMode('edit')
    } else {
      handleSaveLocation()
    }
  }

  const isViewMode = locationModalMode === 'view'
  const isLoading = addLocationLoading || updateLocationLoading

  const handleAddressSearch = (value) => {
    setAddressSearchValue(value);

    if (!autocompleteServiceRef.current && window.google) {
      autocompleteServiceRef.current =
        new window.google.maps.places.AutocompleteService();
    }

    if (!value) {
      setAddressOptions([]);
      return;
    }

    autocompleteServiceRef.current.getPlacePredictions(
      {
        input: value,
        componentRestrictions: { country: "NG" },
      },
      (predictions) => {
        const results = predictions
          ? predictions.map((p) => ({
            value: p.description,
            label: p.description,
          }))
          : [];
        setAddressOptions(results);
      }
    );
  };


  const handleAddressSelect = (address) => {
    handleLocationInputChange("address", address);

    // Initialize PlacesService
    if (!window.google) return;

    const service = new window.google.maps.places.PlacesService(
      document.createElement("div")
    );

    service.findPlaceFromQuery(
      {
        query: address,
        fields: ["geometry"],
      },
      (results, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          results &&
          results.length > 0
        ) {
          const location = results[0].geometry.location;

          handleLocationInputChange("latitude", location.lat().toString());
          handleLocationInputChange("longitude", location.lng().toString());
        }
      }
    );
  };


  return (
    <div className="max-w-6xl mx-auto p-6 bg-white">
      {/* Teams Section */}
      <div className="mb-8">
        <div className="flex gap-8">
          <div className="w-1/3">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Teams</h2>
            <p className="text-sm text-gray-600">Set up your teams/order type</p>
          </div>

          <div className="w-2/3">
            {teamsLoading ? (
              <div className="text-center py-8">
                <Loader text="Loading teams" />
              </div>
            ) : teamsError ? (
              <div className="text-center py-8">
                <p className="text-red-500">Error loading teams. Please try again.</p>
              </div>
            ) : teams.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <div className="space-y-4">
                  <div>
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">No teams yet</h3>
                    <p className="text-sm text-gray-500 mt-1">Get started by creating your first team</p>
                  </div>
                  <Button
                    type="primary"
                    onClick={handleOpenModal}
                    className="bg-orange-500 hover:bg-orange-600 border-orange-500 hover:border-orange-600"
                  >
                    + Add Your First Team
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex gap-2 items-start">
                  <div className="flex-1">
                    <CustomSelectField
                      placeholder="Teams/order types..."
                      options={teamOptions}
                      value={selectedTeams}
                      onChange={handleTeamChange}
                      className="w-full"
                      searchable={false}
                      disabled={isDeleteModalVisible}
                      showDropdown={false}
                    />
                  </div>

                  <Button
                    type="primary"
                    onClick={handleOpenModal}
                    disabled={isDeleteModalVisible}
                    className="bg-orange-500 hover:bg-orange-600 border-orange-500 hover:border-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    + Add Team
                  </Button>
                </div>

                {selectedTeams.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600">
                      {selectedTeams.length} team(s) in SFS
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200 mb-8"></div>

      {/* Location Section */}
      <div>
        <div className="flex gap-8">
          <div className="w-1/3">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Location</h2>
            <p className="text-sm text-gray-600">Set up locations</p>
          </div>

          <div className="w-2/3">
            {locationsLoading ? (
              <div className="text-center py-8">
                <Loader text="Loading locations" />
              </div>
            ) : locationsError ? (
              <div className="text-center py-8">
                <p className="text-red-500">Error loading locations. Please try again.</p>
              </div>
            ) : locations.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <div className="space-y-4">
                  <div>
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">No locations yet</h3>
                    <p className="text-sm text-gray-500 mt-1">Get started by adding your first location</p>
                  </div>
                  <Button
                    type="primary"
                    onClick={() => handleOpenLocationModal('create')}
                    className="bg-orange-500 hover:bg-orange-600 border-orange-500 hover:border-orange-600"
                  >
                    + Add Your First Location
                  </Button>
                </div>
              </div>
            ) : (
              <SimpleTable
                title="SFS Branches"
                data={branchesData}
                columns={branchColumns}
                onAdd={() => handleOpenLocationModal('create')}
                onEdit={handleEditBranch}
                onView={handleViewBranch}
                onDelete={handleDeactivateLocation}
                onSelect={handleSelectBranches}
                selectedRowKeys={selectedBranches}
                pagination={true}
                pageSize={5}
                showCheckbox={true}
                showActions={true}
                deleteButtonText={(record) => record.status === 'Active' ? 'Deactivate' : 'Activate'}
                loading={locationsFetching || locationsLoading}
              />
            )}
          </div>
        </div>
      </div>

      {/* Add Team Modal */}
      <Modal
        title="Add New Team"
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={[
          <Button key="cancel" onClick={handleCloseModal}>
            Cancel
          </Button>,
          <Button
            key="add"
            type="primary"
            onClick={handleAddTeam}
            loading={addTeamLoading}
            disabled={!newTeamName.trim()}
            className="bg-orange-500 hover:bg-orange-600 border-orange-500 hover:border-orange-600"
          >
            Add Team
          </Button>
        ]}
        width={500}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Team Name *
            </label>
            <input
              type="text"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              placeholder="Enter team name..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={newTeamDescription}
              onChange={(e) => setNewTeamDescription(e.target.value)}
              placeholder="Enter team description (optional)..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
            />
          </div>
        </div>
      </Modal>

      {/* Delete Team Confirmation Modal */}
      <Modal
        title="Delete Team"
        open={isDeleteModalVisible}
        onCancel={() => {
          setIsDeleteModalVisible(false)
          setTeamToDelete(null)
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setIsDeleteModalVisible(false)
            setTeamToDelete(null)
          }}>
            Cancel
          </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            onClick={() => handleDeleteTeam(teamToDelete?.id)}
            loading={deleteTeamLoading}
          >
            Delete Team
          </Button>
        ]}
        width={400}
      >
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">Are you sure?</h3>
              <p className="text-sm text-gray-500 mt-1">
                This action cannot be undone. The team "{teamToDelete?.name}" will be permanently deleted.
              </p>
            </div>
          </div>
        </div>
      </Modal>

      {/* Location Modal (Create / Edit / View) */}
      <CustomModal
        isOpen={isLocationModalVisible}
        onClose={handleCloseLocationModal}
        title={getModalTitle()}
        subtitle={getModalSubtitle()}
        primaryActionText={getPrimaryActionText()}
        onPrimaryAction={handlePrimaryAction}
        secondaryActionText={locationModalMode === 'view' ? 'Close' : 'Cancel'}
        onSecondaryAction={handleCloseLocationModal}
        loading={isLoading}
        primaryActionDisabled={
          locationModalMode !== 'view' &&
          (!locationFormData.name || !locationFormData.address || !locationFormData.state_id || !locationFormData.lga_id)
        }
        width={600}
        isCenter={true}
      >
        <div className="p-6 space-y-4">
          {/* Location Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location Name {!isViewMode && '*'}
            </label>
            {isViewMode ? (
              <p className="text-gray-900 py-2">{locationFormData.name || '-'}</p>
            ) : (
              <input
                type="text"
                value={locationFormData.name}
                onChange={(e) => handleLocationInputChange('name', e.target.value)}
                placeholder="e.g., Ikotun"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            )}
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address {!isViewMode && '*'}
            </label>
            {isViewMode ? (
              <p className="text-gray-900 py-2">{locationFormData.address || '-'}</p>
            ) : (
              <>
                <LoadScript
                  googleMapsApiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
                  libraries={["places"]}
                >
                  <Select
                    showSearch
                    placeholder="Start typing address..."
                    value={addressSearchValue || locationFormData.address}
                    onSearch={handleAddressSearch}
                    onChange={(value) => {
                      setAddressSearchValue("");
                      handleAddressSelect(value); // <-- auto generates lat/lng
                    }}
                    filterOption={false}
                    options={addressOptions}
                    notFoundContent={null}
                    className="w-full"
                  />
                </LoadScript>


                <p className="text-xs text-gray-500 mt-1">
                  Start typing to see suggestions. Selecting an address will auto-fill coordinates.
                </p>
              </>
            )}
          </div>

          {/* Latitude & Longitude */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Latitude {locationFormData.latitude && !isViewMode && <span className="text-green-600 text-xs">(Auto-filled)</span>}
              </label>
              {isViewMode ? (
                <p className="text-gray-900 py-2">{locationFormData.latitude || '-'}</p>
              ) : (
                <input
                  type="number"
                  step="any"
                  value={locationFormData.latitude}
                  onChange={(e) => handleLocationInputChange('latitude', e.target.value)}
                  placeholder="e.g., 6.."
                  readOnly={true}
                  disabled={true}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${locationFormData.latitude ? 'bg-green-50 border-green-200' : ''
                    }`}
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Longitude {locationFormData.longitude && !isViewMode && <span className="text-green-600 text-xs">(Auto-filled)</span>}
              </label>
              {isViewMode ? (
                <p className="text-gray-900 py-2">{locationFormData.longitude || '-'}</p>
              ) : (
                <input
                  type="number"
                  step="any"
                  value={locationFormData.longitude}
                  onChange={(e) => handleLocationInputChange('longitude', e.target.value)}
                  placeholder="e.g., 3.3792"
                  readOnly={true}
                  disabled={true}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent ${locationFormData.longitude ? 'bg-green-50 border-green-200' : ''
                    }`}
                />
              )}
            </div>
          </div>

          {/* State */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State {!isViewMode && '*'}
            </label>
            {isViewMode ? (
              <p className="text-gray-900 py-2">
                {stateOptions.find(s => s.value === locationFormData.state_id)?.label || '-'}
              </p>
            ) : (
              <Select
                placeholder="Select state"
                value={locationFormData.state_id}
                onChange={(value) => handleLocationInputChange('state_id', value)}
                options={stateOptions}
                className="w-full"
                loading={statesLoading}
                allowClear
                onClear={() => handleLocationInputChange('state_id', null)}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            )}
          </div>

          {/* LGA */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              LGA {!isViewMode && '*'}
            </label>
            {isViewMode ? (
              <p className="text-gray-900 py-2">
                {lgaOptions.find(l => l.value === locationFormData.lga_id)?.label || '-'}
              </p>
            ) : (
              <>
                <Select
                  placeholder={locationFormData.state_id ? "Select LGA" : "Please select a state first"}
                  value={locationFormData.lga_id}
                  onChange={(value) => handleLocationInputChange('lga_id', value)}
                  options={lgaOptions}
                  className="w-full"
                  loading={isLoadingLgas || isFetchingLgas}
                  disabled={!locationFormData.state_id}
                  allowClear
                  onClear={() => handleLocationInputChange('lga_id', null)}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  notFoundContent={
                    !locationFormData.state_id
                      ? "Please select a state first"
                      : isLoadingLgas || isFetchingLgas
                        ? "Loading LGAs..."
                        : "No LGAs found"
                  }
                />
                {!locationFormData.state_id && (
                  <p className="text-xs text-gray-500 mt-1">
                    Select a state to see available LGAs
                  </p>
                )}
              </>
            )}
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            {isViewMode ? (
              <p className="text-gray-900 py-2">{locationFormData.phone_number || '-'}</p>
            ) : (
              <input
                type="tel"
                value={locationFormData.phone_number}
                onChange={(e) => handleLocationInputChange('phone_number', e.target.value)}
                placeholder="e.g., 0809977664"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            )}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            {isViewMode ? (
              <StatusPill status={locationFormData.status === 1 ? 'Active' : 'Inactive'} />
            ) : (
              <Select
                value={locationFormData.status}
                onChange={(value) => handleLocationInputChange('status', value)}
                options={[
                  { value: 1, label: 'Active' },
                  { value: 0, label: 'Inactive' }
                ]}
                className="w-full"
              />
            )}
          </div>
        </div>
      </CustomModal>

      {/* Location Status Toggle Confirmation Modal */}
      <CustomModal
        isOpen={isStatusModalVisible}
        onClose={handleCancelStatusToggle}
        title={locationToToggle?.status === 'Active' ? 'Deactivate Location' : 'Activate Location'}
        subtitle={`Are you sure you want to ${locationToToggle?.status === 'Active' ? 'deactivate' : 'activate'} this location?`}
        primaryActionText={locationToToggle?.status === 'Active' ? 'Deactivate' : 'Activate'}
        onPrimaryAction={handleConfirmStatusToggle}
        secondaryActionText="Cancel"
        onSecondaryAction={handleCancelStatusToggle}
        loading={toggleLocationLoading}
        confirm={{
          type: 'caution',
          message: `This will ${locationToToggle?.status === 'Active' ? 'deactivate' : 'activate'} the location "${locationToToggle?.lga}". The location will ${locationToToggle?.status === 'Active' ? 'no longer be available' : 'become available'} for use.`
        }}
        width={500}
        isCenter={true}
      />
    </div>
  )
}

export default TeamsandLocations